import { provideRouter, Routes } from '@angular/router';
import { autoLoginPartialRoutesGuard } from 'angular-auth-oidc-client';
import { authCanMatchGuard } from '@shared/guards/auth-can-match.guard';
import { hasPermission } from '@tiltgroup/web-components';
import { Permission } from '@shared/interfaces/permission';
import { teamProfileOverviewResolver } from './pages/teams/pages/team-profile/resolvers/team-profile-overview.resolver';
import { hasTeamGuard } from '@shared/guards/has-team.guard';
import { sideMenuPurgeGuard } from '@shared/guards/side-menu-purge.guard';
import { ALERT_SIDEBAR_PATH } from '@components/alert-sidebar/alert-sidebar.constants';
import { DOCUMENT_SIDEBAR_PATH } from '@components/document-sidebar/document-sidebar.constants';
import { APP_REDIRECT_ROUTES } from './app-redirects';
import { privateAppStartupGuard } from './private-layout/private-app-startup.guard';
import { assistantName } from '@shared/white-label.config';

export enum AppRoutes {
  DASHBOARD = 'dashboard',
  SHIPMENTS = 'shipments',
  LOADS = 'loads',
  QUOTES = 'quotes',
  BIDS = 'bids',
  OVERVIEW = 'overview',
  CUSTOMERS = 'customers',
  CARRIERS = 'carriers',
  ORGANIZATION_SALES_REPRESENTATIVES = 'organization/sales-representatives',
  ORGANIZATION_TEAMS = 'organization/teams',
  COMMISSIONS = 'finance/commissions',
  CLAIMS = 'finance/claims',
  CHECK_CALLS = 'check-calls',
  TASKS = 'tasks',
  TENDERS = 'tenders',
  PERSONAL_SETTINGS = 'personal-settings',
  WORKSPACE_SETTINGS = 'workspace-settings',
  TEAM_PERFORMANCE = 'team-performance',
  TOOLS = 'tools',
  REPORTS = 'reports',
}

/** The signed-in app, rendered inside PrivateLayoutComponent. */
export const PRIVATE_ROUTES: Routes = [
  { path: '', redirectTo: AppRoutes.DASHBOARD, pathMatch: 'full' },
  {
    path: 'loads',
    redirectTo: 'shipments',
    pathMatch: 'full',
  },
  {
    path: AppRoutes.DASHBOARD,
    loadComponent: () =>
      import('./pages/dashboard/dashboard.component').then(
        (m) => m.DashboardComponent
      ),
    canActivate: [autoLoginPartialRoutesGuard],
  },
  {
    path: AppRoutes.TEAM_PERFORMANCE,
    resolve: { teamID: teamProfileOverviewResolver },
    loadChildren: () =>
      import('./pages/teams/pages/team-profile/team-profile-routing').then(
        (m) => m.TEAM_PROFILE_ROUTES
      ),
    canMatch: [
      authCanMatchGuard,
      () => hasPermission(Permission.VIEW_TEAM),
      () => hasTeamGuard(undefined),
    ],
  },
  {
    path: 'shipments',
    loadChildren: () =>
      import('./pages/shipments/shipments-routing').then(
        (m) => m.SHIPMENT_ROUTES
      ),
    canMatch: [authCanMatchGuard, () => hasPermission(Permission.VIEW_LOAD)],
  },
  {
    path: 'tenders',
    loadChildren: () =>
      import('./pages/tenders/tenders-routing').then((m) => m.TENDERS_ROUTES),
    canMatch: [authCanMatchGuard, () => hasPermission(Permission.VIEW_LOAD)],
  },
  {
    path: AppRoutes.QUOTES,
    loadChildren: () =>
      import('./pages/quotes/quotes-routing').then((m) => m.QUOTES_ROUTES),
    canMatch: [
      authCanMatchGuard,
      () => hasPermission(Permission.VIEW_LTL_QUOTE_REQUEST),
    ],
    canDeactivate: [sideMenuPurgeGuard],
  },
  {
    path: AppRoutes.CUSTOMERS,
    loadChildren: () =>
      import('./pages/clients/clients-routing').then((m) => m.CLIENT_ROUTES),
    canMatch: [authCanMatchGuard, () => hasPermission(Permission.VIEW_CLIENT)],
    title: 'Customers',
  },
  {
    path: 'carriers',
    loadChildren: () =>
      import('./pages/carriers/carriers-routing').then((m) => m.CARRIER_ROUTES),
    canMatch: [authCanMatchGuard, () => hasPermission(Permission.VIEW_CARRIER)],
  },
  {
    path: 'drivers',
    loadChildren: () =>
      import('./pages/drivers/drivers-routing').then((m) => m.DRIVERS_ROUTES),
    canMatch: [authCanMatchGuard, () => hasPermission(Permission.VIEW_DRIVER)],
  },
  {
    path: 'organization/sales-representatives',
    loadComponent: () =>
      import(
        './pages/organization/sales-representatives/sales-representatives.component'
      ).then((m) => m.SalesRepresentativesComponent),
    canActivate: [
      autoLoginPartialRoutesGuard,
      () => hasPermission(Permission.VIEW_SALES_REPRESENTATIVE),
    ],
  },
  {
    path: 'organization/teams',
    loadChildren: () =>
      import('./pages/teams/teams-routing').then((m) => m.TEAM_ROUTES),
    canActivate: [
      autoLoginPartialRoutesGuard,
      () => hasPermission(Permission.VIEW_TEAM),
    ],
  },
  {
    path: AppRoutes.COMMISSIONS,
    loadChildren: () =>
      import(
        './pages/finance/commission-summary/commission-summary-routing'
      ).then((m) => m.COMMISSION_SUMMARY_ROUTE),
    title: 'Commissions',
    canActivate: [
      autoLoginPartialRoutesGuard,
      () => hasPermission(Permission.VIEW_COMMISSION_SUMMARY),
    ],
  },
  {
    path: AppRoutes.CLAIMS,
    loadComponent: () =>
      import('./pages/finance/claims/claims.component').then(
        (m) => m.ClaimsComponent
      ),
    canActivate: [
      autoLoginPartialRoutesGuard,
      () => hasPermission(Permission.VIEW_SHIPMENT_CLAIM),
    ],
  },
  {
    path: AppRoutes.PERSONAL_SETTINGS,
    loadChildren: () =>
      import('./pages/personal-settings/personal-settings-routing').then(
        (m) => m.PERSONAL_SETTINGS_ROUTES
      ),
    title: 'Personal settings',
    canActivate: [autoLoginPartialRoutesGuard],
  },
  {
    path: AppRoutes.WORKSPACE_SETTINGS,
    loadChildren: () =>
      import('./pages/workspace-settings/workspace-settings-routing').then(
        (m) => m.WORKSPACE_SETTINGS_ROUTING
      ),
    title: 'Workspace settings',
    canActivate: [autoLoginPartialRoutesGuard],
  },

  ...APP_REDIRECT_ROUTES,
  {
    path: AppRoutes.TOOLS,
    loadChildren: () =>
      import('./pages/tools/tools-routing').then((m) => m.TOOLS_ROUTES),
    canActivate: [autoLoginPartialRoutesGuard],
  },
  {
    path: AppRoutes.REPORTS,
    loadChildren: () =>
      import('./pages/reports/reports-routing').then((m) => m.REPORT_ROUTES),
    canMatch: [authCanMatchGuard, () => hasPermission(Permission.VIEW_REPORT)],
    title: 'Reports',
  },

  /**
   * Side routes
   */
  {
    path: `${AppRoutes.QUOTES}/:id`,
    outlet: 'side',
    loadChildren: () =>
      import('./pages/quotes/pages/quote-profile/quote-profile-routing').then(
        (m) => m.QUOTE_PROFILE_ROUTES
      ),
    canMatch: [
      authCanMatchGuard,
      () => hasPermission(Permission.VIEW_LTL_QUOTE_REQUEST),
    ],
  },
  {
    path: `${AppRoutes.CHECK_CALLS}/:id`,
    outlet: 'side',
    loadChildren: () =>
      import('./shared/components/check-calls/check-calls.routing').then(
        (m) => m.CHECK_CALL_ROUTES
      ),
    canMatch: [authCanMatchGuard],
  },
  {
    path: `${AppRoutes.TASKS}/:id`,
    outlet: 'side',
    loadChildren: () =>
      import('./shared/components/task-sidebar/task-sidebar.routing').then(
        (m) => m.TASK_SIDEBAR_ROUTES
      ),
    canMatch: [authCanMatchGuard],
  },
  {
    path: `${ALERT_SIDEBAR_PATH}/:id`,
    outlet: 'side',
    loadChildren: () =>
      import('./shared/components/alert-sidebar/alert-sidebar.routing').then(
        (m) => m.ALERT_SIDEBAR_ROUTES
      ),
    canMatch: [authCanMatchGuard],
  },
  {
    path: `${DOCUMENT_SIDEBAR_PATH}/:id`,
    outlet: 'side',
    loadChildren: () =>
      import(
        './shared/components/document-sidebar/document-sidebar.routing'
      ).then((m) => m.DOCUMENT_SIDEBAR_ROUTES),
    canMatch: [authCanMatchGuard],
  },
  {
    path: `${AppRoutes.BIDS}/:id`,
    outlet: 'side',
    loadChildren: () =>
      import(
        './pages/shipments/pages/shipment-profile/pages/shipment-profile-bids/components/shipment-profile-bids-details/shipment-profile-bids-details-routing'
      ).then((m) => m.SHIPMENT_PROFILE_BIDS_DETAILS_ROUTES),
    canMatch: [
      authCanMatchGuard,
      () => hasPermission(Permission.VIEW_SHIPMENT_NEGOTIATION),
    ],
  },

  {
    path: 'unauthorized',
    loadComponent: () =>
      import('./pages/unauthorized/unauthorized.component').then(
        (m) => m.UnauthorizedComponent
      ),
  },
  {
    path: 'forbidden',
    loadComponent: () =>
      import('./pages/unauthorized/unauthorized.component').then(
        (m) => m.UnauthorizedComponent
      ),
  },
  {
    path: 'callback',
    loadComponent: () =>
      import('./pages/callback/callback.component').then(
        (m) => m.CallbackComponent
      ),
  },
  // An unknown URL (an outdated bookmark or a mistyped link) lands on the
  // dashboard instead of throwing NG04002 (NEXUS-UI-5). Logged-out and
  // permission-denied navigations redirect from their guards and never get here.
  { path: '**', redirectTo: AppRoutes.DASHBOARD },
];

/** Pages people outside Nexus open from an email, such as a signing link: no app chrome, and never a login. */
export const PUBLIC_ROUTES: Routes = [
  {
    path: 'sign/:token',
    title: 'Sign document',
    loadComponent: () =>
      import('./pages/document-signing/document-signing.component').then(
        (m) => m.DocumentSigningComponent
      ),
  },
];

export const APP_ROUTES: Routes = [
  {
    path: 'public',
    children: PUBLIC_ROUTES,
    title: '',
    data: { overridePreviousTitle: true },
  },
  // Framed by the browser extension, which signs it in; outside the app's layout and login.
  {
    path: 'embed/assistant',
    loadComponent: () =>
      import('./pages/embed-assistant/embed-assistant.component').then(
        (m) => m.EmbedAssistantComponent
      ),
    title: assistantName,
  },
  // Signing links sent before public pages moved under /public.
  {
    path: 'sign/:token',
    redirectTo: 'public/sign/:token',
  },
  {
    path: '',
    canMatch: [privateAppStartupGuard],
    // Loaded lazily: its navigation imports AppRoutes from this file.
    loadComponent: () =>
      import('./private-layout/private-layout.component').then(
        (m) => m.PrivateLayoutComponent
      ),
    children: PRIVATE_ROUTES,
  },
];

export const routerConfig = provideRouter(APP_ROUTES);
